import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import Loading from '../ui/Loading'

const CATEGORY_OPTIONS = ['상해', '질병', '교통사고', '화재', '건강/진료', '간병', '치매', '연금', '일반', '기타']
const FONT_OPTIONS = [
    { value: 'NanumGothic', label: '나눔고딕(한글)' },
    { value: 'SansSerif', label: 'SansSerif(기본)' },
    { value: 'NotoSansKR', label: 'Noto Sans KR(한글)' },
    { value: 'Serif', label: 'Serif' },
    { value: 'Monospaced', label: 'Monospaced' },
]

function ImageBBoxSelector({ file, onChange }) {
    const imgRef = React.useRef(null)
    const [objectUrl, setObjectUrl] = React.useState(null)
    const [dragging, setDragging] = React.useState(false)
    const [start, setStart] = React.useState(null)
    const [rect, setRect] = React.useState(null)
    const rectRef = React.useRef(null)
    const [zoom, setZoom] = React.useState(1.6)

    React.useEffect(() => {
        if (!file) {
            setObjectUrl(null)
            setRect(null)
            rectRef.current = null
            setZoom(1.6)
            onChange && onChange(null)
            return
        }
        const u = URL.createObjectURL(file)
        setObjectUrl(u)
        return () => { try { URL.revokeObjectURL(u) } catch { } }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file])

    const toOrigRect = React.useCallback((viewRect) => {
        const img = imgRef.current
        if (!img || !viewRect) return null
        const { naturalWidth, naturalHeight } = img
        const { width: vwScaled, height: vhScaled } = img.getBoundingClientRect()
        const vw = vwScaled / zoom
        const vh = vhScaled / zoom
        if (!naturalWidth || !naturalHeight || !vw || !vh) return null
        const sx = naturalWidth / vw
        const sy = naturalHeight / vh
        return {
            x: Math.round(viewRect.x * sx),
            y: Math.round(viewRect.y * sy),
            w: Math.round(viewRect.w * sx),
            h: Math.round(viewRect.h * sy),
        }
    }, [zoom])

    const getLocalPoint = (e) => {
        const img = imgRef.current
        if (!img) return null
        const r = img.getBoundingClientRect()
        const x = (e.clientX - r.left) / zoom
        const y = (e.clientY - r.top) / zoom
        const maxX = r.width / zoom
        const maxY = r.height / zoom
        return { x: Math.max(0, Math.min(x, maxX)), y: Math.max(0, Math.min(y, maxY)) }
    }

    const onDown = (e) => {
        if (!imgRef.current) return
        const p = getLocalPoint(e)
        if (!p) return
        setDragging(true)
        setStart(p)
        const next = { x: p.x, y: p.y, w: 0, h: 0 }
        rectRef.current = next
        setRect(next)
    }
    const onMove = (e) => {
        if (!dragging || !start) return
        const p = getLocalPoint(e)
        if (!p) return
        const x = Math.min(start.x, p.x)
        const y = Math.min(start.y, p.y)
        const w = Math.abs(p.x - start.x)
        const h = Math.abs(p.y - start.y)
        const next = { x, y, w, h }
        rectRef.current = next
        setRect(next)
    }
    const onUp = () => {
        if (!dragging) return
        setDragging(false)
        setStart(null)
        const cur = rectRef.current
        const r = cur && cur.w >= 2 && cur.h >= 2 ? cur : null
        const orig = toOrigRect(r)
        onChange && onChange(orig)
    }

    if (!file) return null

    return (
        <div className="mt-3">
            <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-sm text-slate-600">덮어쓸 영역을 드래그로 선택하세요.</div>
                <div className="flex items-center gap-2 text-sm">
                    <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.2) * 10) / 10))}>-</button>
                    <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
                    <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setZoom(z => Math.min(3, Math.round((z + 0.2) * 10) / 10))}>+</button>
                    <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setZoom(1.6)}>reset</button>
                    <span className="text-xs text-slate-500 w-[54px] text-right">{Math.round(zoom * 100)}%</span>
                </div>
            </div>
            <div className="rounded border bg-slate-50 p-2 overflow-auto max-h-[70vh]">
                <div className="relative inline-block select-none" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                    <img
                        ref={imgRef}
                        src={objectUrl || ''}
                        alt="preview"
                        className="max-w-full max-h-[70vh] rounded border cursor-crosshair"
                        onMouseDown={onDown}
                        onMouseMove={onMove}
                        onMouseUp={onUp}
                        onMouseLeave={onUp}
                        draggable={false}
                    />
                    {rect && (
                        <div
                            className="absolute border-2 border-brand-sky bg-brand-sky/10"
                            style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, pointerEvents: 'none' }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default function SalesImagesCreatePage() {
    const nav = useNavigate()
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [previewUrl, setPreviewUrl] = React.useState(null)
    const [previewOpen, setPreviewOpen] = React.useState(false)

    const [templateType, setTemplateType] = React.useState('NAME') // NAME | MESSAGE
    const [title, setTitle] = React.useState('')
    const [category, setCategory] = React.useState('교통사고')
    const [purpose, setPurpose] = React.useState('')
    const [file, setFile] = React.useState(null)

    const [bbox, setBbox] = React.useState(null)
    const [textColor, setTextColor] = React.useState('#111827')
    const [fontFamily, setFontFamily] = React.useState('NanumGothic')
    const [fontSize, setFontSize] = React.useState(0) // 0=auto

    const [messageTemplate, setMessageTemplate] = React.useState('{고객명} 고객님 안녕하세요, 위험을 보호해 드리는 {설계사} 입니다.')
    const [messageBgColor, setMessageBgColor] = React.useState('#FFFFFF')
    const [messagePosition, setMessagePosition] = React.useState('TOP')
    const [messageBoxHeight, setMessageBoxHeight] = React.useState(120)

    React.useEffect(() => {
        return () => {
            try { if (previewUrl) URL.revokeObjectURL(previewUrl) } catch { }
        }
    }, [previewUrl])

    const createTemplate = async (e) => {
        e.preventDefault()
        if (!file) return setError('이미지 파일을 선택하세요.')
        if (!category) return setError('카테고리를 선택하세요.')
        if (!purpose.trim()) return setError('용도를 입력하세요.')
        if (!/^#[0-9a-fA-F]{6}$/.test(String(textColor || ''))) return setError('텍스트 색상 형식이 올바르지 않습니다.')

        if (templateType === 'NAME') {
            if (!bbox) return setError('덮어쓸 영역을 드래그로 선택하세요.')
        } else {
            if (!messageTemplate || !messageTemplate.trim()) return setError('문구를 입력하세요.')
            if (!/^#[0-9a-fA-F]{6}$/.test(String(messageBgColor || ''))) return setError('배경색 형식이 올바르지 않습니다.')
        }

        setLoading(true)
        setError(null)
        try {
            const fd = new FormData()
            fd.append('file', file)
            fd.append('title', title || '고객발송용 이미지')
            fd.append('category', category)
            fd.append('purpose', purpose)
            fd.append('textColor', String(textColor))
            fd.append('fontFamily', String(fontFamily))
            fd.append('fontSize', String(fontSize || 0))

            if (templateType === 'NAME') {
                fd.append('bboxX', String(bbox.x))
                fd.append('bboxY', String(bbox.y))
                fd.append('bboxW', String(bbox.w))
                fd.append('bboxH', String(bbox.h))
            } else {
                fd.append('messageTemplate', String(messageTemplate))
                fd.append('messageBgColor', String(messageBgColor))
                fd.append('messagePosition', String(messagePosition))
                fd.append('messageBoxHeight', String(messageBoxHeight || 120))
            }

            await api.post('/sales-images/templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            nav('/sales-images')
        } catch (_) {
            setError('템플릿 생성에 실패했습니다.')
        } finally {
            setLoading(false)
        }
    }

    const canPreview = () => {
        if (!file) return false
        if (!category) return false
        if (!purpose.trim()) return false
        if (!/^#[0-9a-fA-F]{6}$/.test(String(textColor || ''))) return false
        if (templateType === 'NAME') return Boolean(bbox && bbox.w && bbox.h)
        if (!messageTemplate || !messageTemplate.trim()) return false
        if (!/^#[0-9a-fA-F]{6}$/.test(String(messageBgColor || ''))) return false
        return true
    }

    const preview = async () => {
        if (!canPreview()) return
        setLoading(true)
        setError(null)
        try {
            const fd = new FormData()
            fd.append('file', file)
            fd.append('textColor', String(textColor))
            fd.append('fontFamily', String(fontFamily))
            fd.append('fontSize', String(fontSize || 0))
            if (templateType === 'NAME') {
                fd.append('bboxX', String(bbox.x))
                fd.append('bboxY', String(bbox.y))
                fd.append('bboxW', String(bbox.w))
                fd.append('bboxH', String(bbox.h))
            } else {
                fd.append('messageTemplate', String(messageTemplate))
                fd.append('messageBgColor', String(messageBgColor))
                fd.append('messagePosition', String(messagePosition))
                fd.append('messageBoxHeight', String(messageBoxHeight || 120))
            }
            const res = await api.post('/sales-images/preview', fd, { responseType: 'blob', headers: { 'Content-Type': 'multipart/form-data' } })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
            const url = URL.createObjectURL(blob)
            try { if (previewUrl) URL.revokeObjectURL(previewUrl) } catch { }
            setPreviewUrl(url)
            setPreviewOpen(true)
        } catch (_) {
            setError('미리보기 생성에 실패했습니다.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-2xl font-bold">템플릿 생성</div>
                    <div className="text-sm text-slate-600 mt-1">카테고리/용도와 템플릿 옵션을 입력해 템플릿을 등록합니다.</div>
                </div>
                <Link className="rounded border px-4 py-2 text-sm hover:bg-slate-50" to="/sales-images">목록으로</Link>
            </div>

            {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {loading && <Loading />}

            <div className="rounded border p-4 bg-white">
                <form onSubmit={createTemplate} className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-slate-600">템플릿 종류</div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="tplType" checked={templateType === 'NAME'} onChange={() => setTemplateType('NAME')} />
                            이름 덮어쓰기(좌표)
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="tplType" checked={templateType === 'MESSAGE'} onChange={() => setTemplateType('MESSAGE')} />
                            문구 배너(상단/하단)
                        </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <div className="text-sm text-slate-600 mb-1">제목</div>
                            <input className="w-full rounded border px-3 py-2 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 교통사고 안내 이미지" />
                        </div>
                        <div>
                            <div className="text-sm text-slate-600 mb-1">이미지 파일(png/jpg)</div>
                            <input type="file" accept="image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            <div className="text-xs text-slate-500 mt-1">
                                템플릿 원본 이미지는 업로드 후 <b>1개월</b> 보관됩니다. 1개월 경과 시 원본 이미지는 삭제되고 템플릿은 비활성화됩니다.
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <div className="text-sm text-slate-600 mb-1">카테고리</div>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
                                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-sm text-slate-600 mb-1">용도</div>
                            <input className="w-full rounded border px-3 py-2 text-sm" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="예: 카카오 안내 이미지(고객 톡 발송용)" />
                        </div>
                    </div>

                    {/* 요청사항: 텍스트색/배경색/위치/박스높이 동일 row */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                            <div className="text-sm text-slate-600 mb-1">텍스트</div>
                            <div className="flex items-center gap-2">
                                <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="h-9 w-14 rounded border bg-white" />
                                <input className="rounded border px-3 py-2 text-sm w-full" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <div className="text-sm text-slate-600 mb-1">배경</div>
                            <div className="flex items-center gap-2">
                                <input type="color" value={messageBgColor} onChange={(e) => setMessageBgColor(e.target.value)} className="h-9 w-14 rounded border bg-white" disabled={templateType !== 'MESSAGE'} />
                                <input className="rounded border px-3 py-2 text-sm w-full" value={messageBgColor} onChange={(e) => setMessageBgColor(e.target.value)} disabled={templateType !== 'MESSAGE'} />
                            </div>
                        </div>
                        <div>
                            <div className="text-sm text-slate-600 mb-1">위치</div>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={messagePosition} onChange={(e) => setMessagePosition(e.target.value)} disabled={templateType !== 'MESSAGE'}>
                                <option value="TOP">상단</option>
                                <option value="BOTTOM">하단</option>
                            </select>
                        </div>
                        <div>
                            <div className="text-sm text-slate-600 mb-1">박스 높이</div>
                            <input type="number" min="60" max="500" className="w-full rounded border px-3 py-2 text-sm" value={messageBoxHeight} onChange={(e) => setMessageBoxHeight(Number(e.target.value))} disabled={templateType !== 'MESSAGE'} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <div className="text-sm text-slate-600 mb-1">폰트</div>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
                                {FONT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-sm text-slate-600 mb-1">글자 크기(0=자동)</div>
                            <input type="number" min="0" max="200" className="w-full rounded border px-3 py-2 text-sm" value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} />
                        </div>
                    </div>

                    {templateType === 'NAME' && (
                        <>
                            <ImageBBoxSelector file={file} onChange={setBbox} />
                            {bbox && <div className="text-xs text-slate-500">선택 좌표(원본 기준): x={bbox.x}, y={bbox.y}, w={bbox.w}, h={bbox.h}</div>}
                        </>
                    )}

                    {templateType === 'MESSAGE' && (
                        <div className="rounded border bg-slate-50 p-3">
                            <div className="text-sm font-medium mb-2">문구 템플릿</div>
                            <textarea className="w-full rounded border px-3 py-2 text-sm min-h-[100px]" value={messageTemplate} onChange={(e) => setMessageTemplate(e.target.value)} />
                            <div className="text-xs text-slate-500 mt-1">
                                치환: <code>{'{고객명}'}</code>, <code>{'{설계사}'}</code>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                            disabled={!canPreview() || loading}
                            onClick={preview}
                        >
                            미리보기(홍길동)
                        </button>
                        <button type="submit" className="rounded bg-brand-navy text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50" disabled={loading}>
                            저장
                        </button>
                        <Link className="rounded border px-4 py-2 text-sm hover:bg-slate-50" to="/sales-images">취소</Link>
                    </div>
                </form>
            </div>

            {previewOpen && previewUrl && (
                <div className="fixed inset-0 z-[110]">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setPreviewOpen(false)} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="w-full max-w-4xl rounded-lg bg-white shadow-lg border">
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div className="font-semibold">미리보기 (고객명: 홍길동)</div>
                                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setPreviewOpen(false)}>
                                    닫기
                                </button>
                            </div>
                            <div className="p-4">
                                <img src={previewUrl} alt="preview" className="w-full max-h-[70vh] object-contain rounded border bg-slate-50" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

